import { forwardRef, ReactNode } from 'react';

import classNames from 'classnames';

const TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'] as const;

export const NAMES = [
  'display-500',
  'display-600',
  'display-800',
  'display-1000',
  'display-1400',
  'display-1800',
  'display-2200',
  'heading-350',
  'heading-400',
  'heading-500',
  'heading-600',
  'heading-1000',
  'heading-1300',
  'heading-1600',
  'body-300',
  'body-350',
  'body-400',
  'body-450',
  'body-550',
  'tag-300',
  'tag-350',
  'condensed-600',
  'condensed-750',
  'condensed-1000',
] as const;

export type TypographyNameType = (typeof NAMES)[number];
export type TypographyTagType = (typeof TAGS)[number];

export interface TypographyProps {
  /** Which HTML tag to use for this node? */
  tag?: TypographyTagType;

  /** The name of the type variant */
  name?: TypographyNameType;

  /** The actual content */
  children: ReactNode;

  /** Any additional classes */
  className?: string;

  /** Standard html id attribute */
  id?: string;
}

/*
Intended use:

- Use <Typography> component for out-of-the-box consistent type treatments
- If responsive override needed, use classNames eg <span className="text-display-600 md:text-display-1400">...
*/
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  function Typography(
    { tag = 'span', name = 'body-400', children, className = '', id = '' },
    ref,
  ) {
    const Tag: any = tag;

    const baseClasses = classNames(
      name.startsWith('display') && [
        'font-serif',
        name === 'display-500' && 'text-display-500',
        name === 'display-600' && 'text-display-600',
        name === 'display-800' && 'text-display-800',
        name === 'display-1000' && 'text-display-800 md:text-display-1000',
        name === 'display-1400' && 'text-display-1000 md:text-display-1400',
        name === 'display-1800' && 'text-display-1400 md:text-display-1800',
        name === 'display-2200' && 'text-display-1400 md:text-display-2200',
      ],

      name.startsWith('heading') && [
        'font-sans',
        name === 'heading-350' && 'text-heading-350',
        name === 'heading-400' && 'text-heading-400',
        name === 'heading-500' && 'text-heading-500',
        name === 'heading-600' && 'text-heading-600',
        name === 'heading-1000' &&
          'text-heading-850 tracking-tight md:text-heading-1000',
        name === 'heading-1300' &&
          'text-heading-1000 tracking-tight md:text-heading-1300',
        name === 'heading-1600' &&
          'text-heading-1300 tracking-tight md:text-heading-1600',
      ],

      name.startsWith('body') && [
        'font-sans',
        name === 'body-300' && 'text-body-300',
        name === 'body-350' && 'text-body-350',
        name === 'body-400' && 'text-body-400',
        name === 'body-450' && 'text-body-450',
        name === 'body-550' && 'text-body-450 md:text-body-550',
      ],

      name.startsWith('tag') && [
        'font-sans uppercase',
        name === 'tag-300' && 'text-tag-300',
        name === 'tag-350' && 'text-tag-350',
      ],

      name.startsWith('condensed') && [
        'font-numbers-sans',
        name === 'condensed-600' && 'text-condensed-600',
        name === 'condensed-750' && 'text-condensed-750',
        name === 'condensed-1000' && 'text-condensed-1000',
      ],
    );

    return (
      <Tag id={id} ref={ref} className={classNames(baseClasses, className)}>
        {children}
      </Tag>
    );
  },
);
